import React, { FunctionComponent, useCallback, useContext, useMemo, useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { ScreenSize, UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { ISelectableRealestate } from "src/stores/RealestateStore";
import { AddRealestatesModal } from "src/components/realestates/add-realestates-modal/AddRealestatesModal";
import styles from "./NewsRealestatesSection.module.css";

type NewsRealestatesSectionProps = {
    realestates: ISelectableRealestate[];
    isCondominium: boolean;
    onChange: (realestates: ISelectableRealestate[]) => void;
};

const COLUMN_WIDTHS = [1, 1, 0.1];
const REALESTATES_PER_PAGE = 10;

const NewsRealestatesSectionBase: FunctionComponent<NewsRealestatesSectionProps> = ({
    realestates,
    isCondominium,
    onChange
}) => {
    const { t } = useTranslation();
    const { uiStore } = useContext(RootStoreContext);
    const [isAddRealestatesModalDisplayed, setIsAddRealestatesModalDisplayed] = useState<boolean>(false);

    const selectedRealestateIds = useMemo(() => realestates.map((realestate) => realestate.id), [realestates]);
    // A news always needs at least one realestate.
    const canRemoveRealestate = realestates.length > 1;

    const onAddRealestates = useCallback(
        (addedRealestates: ISelectableRealestate[]) => {
            onChange([...realestates, ...addedRealestates]);
        },
        [onChange, realestates]
    );

    const onRemoveRealestate = useCallback(
        (removedRealestate: ISelectableRealestate) => () => {
            onChange(realestates.filter((realestate) => realestate.id !== removedRealestate.id));
        },
        [onChange, realestates]
    );

    const onOpenAddRealestatesModal = useCallback(() => {
        setIsAddRealestatesModalDisplayed(true);
    }, []);

    const onCloseAddRealestatesModal = useCallback(() => {
        setIsAddRealestatesModalDisplayed(false);
    }, []);

    const renderRemoveIcon = useCallback(
        (realestate: ISelectableRealestate) =>
            canRemoveRealestate ? (
                <UI.Icon
                    icon={UI.SVGIcon.Trash}
                    onClick={onRemoveRealestate(realestate)}
                    className={styles.RemoveIcon}
                />
            ) : undefined,
        [canRemoveRealestate, onRemoveRealestate]
    );

    const renderItem = useCallback(
        (realestate: ISelectableRealestate, screenSize: ScreenSize) => {
            const listRows =
                screenSize === ScreenSize.MOBILE
                    ? [
                          <UI.List.Row key={`row-mobile-${realestate.id}`}>
                              <div className={styles.MobileRow}>
                                  <div>
                                      <strong>{realestate.name}</strong>
                                      <div className={styles.MobileNumber}>{realestate.number}</div>
                                  </div>
                                  {renderRemoveIcon(realestate)}
                              </div>
                          </UI.List.Row>
                      ]
                    : [
                          <UI.List.Row key={`row-${realestate.id}`}>
                              <UI.List.Cell key="name" colspan={COLUMN_WIDTHS[0]} value={realestate.name} />
                              <UI.List.Cell key="number" colspan={COLUMN_WIDTHS[1]} value={realestate.number} />
                              <UI.List.Cell
                                  key="remove"
                                  colspan={COLUMN_WIDTHS[2]}
                                  value={renderRemoveIcon(realestate)}
                                  className="d-flex justify-content-center"
                              />
                          </UI.List.Row>
                      ];

            return (
                <UI.List.Item
                    key={`item-${realestate.id}`}
                    screenSize={screenSize}
                    rows={listRows}
                    className={styles.ListItem}
                />
            );
        },
        [renderRemoveIcon]
    );

    const renderHeader = useCallback(
        (screenSize: ScreenSize) => {
            if (screenSize === ScreenSize.MOBILE) {
                return <></>;
            }

            const headerCells = [
                <UI.List.Cell key="name" colspan={COLUMN_WIDTHS[0]} value={t("screens.realestate.title")} truncate />,
                <UI.List.Cell
                    key="number"
                    colspan={COLUMN_WIDTHS[1]}
                    value={t("screens.realestate.dashboard.number")}
                    truncate
                />,
                <UI.List.Cell key="remove" colspan={COLUMN_WIDTHS[2]} value="" />
            ];

            return <UI.List.Header rows={headerCells} className="p-0" />;
        },
        [t]
    );

    return (
        <UI.Row>
            <UI.Col>
                <UI.Form.Label>{t("screens.realestate.news_edit.realestates")}</UI.Form.Label>
                <UI.List.BasicList
                    screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
                    items={realestates}
                    renderItem={renderItem}
                    renderHeader={renderHeader}
                    itemsPerPage={REALESTATES_PER_PAGE}
                    emptyListMessage={t("screens.realestate.news_edit.no_realestates_selected").toString()}
                />
                {/* type "button": the section sits inside the news form and must not submit it. */}
                <UI.Button
                    type="button"
                    variant="link"
                    label={`+ ${t("screens.realestate.news_edit.add_realestates")}`}
                    onClick={onOpenAddRealestatesModal}
                    className="pl-0"
                />
                <AddRealestatesModal
                    show={isAddRealestatesModalDisplayed}
                    isCondominium={isCondominium}
                    selectedRealestateIds={selectedRealestateIds}
                    onAdd={onAddRealestates}
                    onClose={onCloseAddRealestatesModal}
                />
            </UI.Col>
        </UI.Row>
    );
};

export const NewsRealestatesSection = observer(NewsRealestatesSectionBase);
